import { Check } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { COLORS } from '@regent/shared';
import { cn } from '@/lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from './overlays';

/** Swatch grid plus a hex field. */
export function ColorPanel({ value, onChange }: { value: string | null; onChange: (color: string) => void }) {
  const [hex, setHex] = useState(value ?? '');
  return (
    <div className="w-52 p-1">
      <div className="grid grid-cols-8 gap-1.5">
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={`Color ${c}`}
            aria-pressed={value?.toLowerCase() === c.toLowerCase()}
            onClick={() => {
              setHex(c);
              onChange(c);
            }}
            className="flex size-5 items-center justify-center rounded-full ring-offset-2 ring-offset-surface hover:ring-2 hover:ring-border-strong"
            style={{ backgroundColor: c }}
          >
            {value?.toLowerCase() === c.toLowerCase() ? (
              <Check className="size-3 text-white" strokeWidth={3} />
            ) : null}
          </button>
        ))}
      </div>
      <label className="mt-2 flex items-center gap-2 text-xs text-fg-muted">
        Hex
        <input
          value={hex}
          aria-label="Hex color"
          placeholder="#5B3FD6"
          maxLength={7}
          onChange={(e) => {
            const next = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`;
            setHex(next);
            if (/^#[0-9a-fA-F]{6}$/.test(next)) onChange(next);
          }}
          className="h-7 w-full rounded-md border border-border-strong bg-surface px-2 font-mono text-xs text-fg"
        />
      </label>
    </div>
  );
}

export function ColorPicker({
  value,
  onChange,
  children,
  className,
}: {
  value: string | null;
  onChange: (color: string) => void;
  /** Custom trigger. Defaults to a round swatch. */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        {children ?? (
          <button
            type="button"
            aria-label="Pick a color"
            className={cn('size-6 rounded-full border border-border-strong', className)}
            style={{ backgroundColor: value ?? 'transparent' }}
          />
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto">
        <ColorPanel value={value} onChange={onChange} />
      </PopoverContent>
    </Popover>
  );
}
